// src/hooks/useBrandStore.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import type { PlanKey } from "../config/plans";
import {
  loadBrandStore,
  saveBrandStore,
  createBrand as createBrandSvc,
  setActiveBrand as setActiveBrandSvc,
  updateActiveBrand as updateActiveBrandSvc,
  getActiveBrand,
} from "../services/brandStore";

import type { BrandProfile, BrandStore } from "../types/brand";
import { canPersistBrandKit, getBrandKitsLimit } from "../config/plans";

function sanitizeStoreForPlan(plan: PlanKey, store: BrandStore): BrandStore {
  const limit = getBrandKitsLimit(plan);
  const allowPersist = canPersistBrandKit(plan);
  const allowAdvanced = plan === "pro" || plan === "premium";

  const brands = Array.isArray((store as any)?.brands) ? [...(store as any).brands] : [];

  // 1) aplicar límite de brands
  let trimmed = brands;
  if (Number.isFinite(limit)) trimmed = brands.slice(0, Math.max(1, limit));

  // 2) sanitizar campos por plan
  const cleaned = trimmed.map((b: any) => {
    const safe: any = {
      ...b,
      name: String(b?.name || "Mi marca"),
      description: String(b?.description || ""),
    };

    // FREE: demo => NO kit, NO advanced
    if (!allowPersist) {
      delete safe.kit;
      delete safe.advanced;
      return safe;
    }

    // BASIC+: permite kit
    // pero BASIC no permite advanced
    if (!allowAdvanced) {
      delete safe.advanced;
    }

    return safe;
  });

  // 3) asegurar activeBrandId válido
  const activeBrandId = (store as any)?.activeBrandId;
  const activeExists = cleaned.some((b: any) => b?.id && b.id === activeBrandId);
  const nextActiveBrandId = activeExists ? activeBrandId : cleaned[0]?.id || null;

  return {
    ...(store as any),
    brands: cleaned,
    activeBrandId: nextActiveBrandId,
  } as BrandStore;
}

export function useBrandStore(userIdOrEmail: string | null, plan: PlanKey) {
  const [store, setStore] = useState<BrandStore | null>(null);

  useEffect(() => {
    if (!userIdOrEmail) return;
    const s = loadBrandStore(userIdOrEmail);
    // ✅ también sanitizamos al cargar (por si cambió el plan)
    setStore(s ? sanitizeStoreForPlan(plan, s) : s);
  }, [userIdOrEmail, plan]);

  const persist = useCallback(
    (next: BrandStore) => {
      if (!userIdOrEmail) return;

      const sanitized = sanitizeStoreForPlan(plan, next);

      setStore(sanitized);
      saveBrandStore(userIdOrEmail, sanitized);
    },
    [userIdOrEmail, plan]
  );

  const activeBrand = useMemo(() => (store ? getActiveBrand(store) : null), [store]);

  const setActiveBrand = useCallback(
    (brandId: string) => {
      if (!store) return;
      persist(setActiveBrandSvc(store, brandId));
    },
    [store, persist]
  );

  const createBrand = useCallback(
    (data?: Partial<Pick<BrandProfile, "name" | "description">>) => {
      if (!store) return { ok: false as const, reason: "no_store" as const };

      const res = createBrandSvc(plan, store, data);
      if (!res.ok) return res;

      persist(res.store);
      return res;
    },
    [store, plan, persist]
  );

  const updateActiveBrand = useCallback(
    (patch: Partial<BrandProfile>) => {
      if (!store) return;
      persist(updateActiveBrandSvc(plan, store, patch));
    },
    [store, plan, persist]
  );

  // ✅ Delete brand (seguro y entendible)
  const deleteBrand = useCallback(
    (brandId: string) => {
      if (!store) return { ok: false as const, reason: "no_store" as const };

      const brands: any[] = Array.isArray((store as any).brands) ? [...(store as any).brands] : [];
      if (brands.length <= 1) {
        return { ok: false as const, reason: "min_one_brand" as const };
      }

      const exists = brands.some((b) => b?.id === brandId);
      if (!exists) return { ok: false as const, reason: "not_found" as const };

      const nextBrands = brands.filter((b) => b?.id !== brandId);

      const wasActive = (store as any).activeBrandId === brandId;
      const nextActiveBrandId = wasActive ? (nextBrands[0]?.id || null) : (store as any).activeBrandId;

      const nextStore = {
        ...(store as any),
        brands: nextBrands,
        activeBrandId: nextActiveBrandId,
      } as BrandStore;

      persist(nextStore);
      return { ok: true as const };
    },
    [store, persist]
  );

  const limit = useMemo(() => getBrandKitsLimit(plan), [plan]);
  const limitReached = useMemo(() => {
    if (!store) return false;
    if (!Number.isFinite(limit)) return false;
    return (store as any).brands.length >= limit;
  }, [store, limit]);

  return {
    store,
    activeBrand,
    setActiveBrand,
    createBrand,
    updateActiveBrand,
    deleteBrand, // ✅ nuevo
    limit,
    limitReached,
  };
}


